import React, { useEffect, useRef, useState } from 'react'
import { Redirect, useLocation } from 'react-router-dom'
import qs from 'query-string'
import { ThemeProvider } from '@material-ui/core/styles'
import themes, { extendTheme } from 'themes'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import Container from '@material-ui/core/Container'
import Typography from '@material-ui/core/Typography'
import RouterLinkButton from 'components/router-link-button'
import { gql, useQuery } from '@apollo/client'

const POLL_INTERVAL_MS = 2000

const STRIPE_CHECKOUT_SESSION_QUERY = gql`
  query StripeCheckoutSession($sessionId: String!) {
    stripeCheckoutSession(sessionId: $sessionId) {
      id
      paymentIntentStatus
    }
  }
`

const CheckoutSuccessPage = () => {
  const { search } = useLocation()
  const { session_id: sessionId } = qs.parse(search)
  const [status, setStatus] = useState('pending')
  const timeoutRef = useRef(null)
  const isPollingRef = useRef(false)
  const { data, error, refetch } = useQuery(STRIPE_CHECKOUT_SESSION_QUERY, {
    variables: { sessionId },
    skip: !sessionId,
    fetchPolicy: 'network-only',
  })

  useEffect(() => {
    if (error) {
      setStatus('errored')
      return
    }

    const session = data?.stripeCheckoutSession
    if (!session) return

    switch (session.paymentIntentStatus) {
      case 'succeeded':
        setStatus('succeeded')
        break
      case 'canceled':
        setStatus('canceled')
        break
      default:
        setStatus('pending')
    }
  }, [data, error])

  useEffect(() => {
    if (!sessionId || status !== 'pending' || isPollingRef.current) return

    isPollingRef.current = true
    let isMounted = true

    const poll = async () => {
      if (!isMounted) return

      try {
        const result = await refetch()
        if (!isMounted) return

        const session = result.data?.stripeCheckoutSession

        if (
          session?.paymentIntentStatus === 'succeeded' ||
          session?.paymentIntentStatus === 'canceled'
        ) {
          setStatus(session?.paymentIntentStatus)
          isPollingRef.current = false
          return
        }

        timeoutRef.current = setTimeout(poll, POLL_INTERVAL_MS)
      } catch {
        setStatus('errored')
        isPollingRef.current = false
      }
    }

    poll()

    return () => {
      isMounted = false
      isPollingRef.current = false
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [sessionId, status, refetch])

  if (!sessionId) return <Redirect to="/" />

  return (
    <ThemeProvider theme={extendTheme(themes.offeringsRefresh)}>
      <Box display="flex" justifyContent="center" mt={8} px={2}>
        <Container maxWidth="sm">
          {status === 'pending' && (
            <>
              <Typography variant="h4" gutterBottom>
                Fetching booking status
              </Typography>
              <Typography variant="body1" gutterBottom>
                This should only take a moment.
              </Typography>
              <Box mt={3}>
                <CircularProgress />
              </Box>
            </>
          )}
          {status === 'succeeded' && (
            <>
              <Typography variant="h4" gutterBottom>
                Booking Confirmed
              </Typography>
              <Typography variant="body1" gutterBottom>
                An email with the details of your booking has been sent to the
                email address provided.
              </Typography>
              <Typography variant="body1" gutterBottom>
                Sign in to send a message to your coach.
              </Typography>
              <Box mt={3}>
                <RouterLinkButton
                  variant="contained"
                  color="primary"
                  to="/club"
                >
                  Message Coach
                </RouterLinkButton>
              </Box>
            </>
          )}
          {status === 'canceled' && (
            <>
              <Typography variant="h4" gutterBottom>
                Booking Failed
              </Typography>
              <Typography variant="body1" gutterBottom>
                We were unable to book your lesson. Your card was not charged.
                Try again or contact your coach for assistance.
              </Typography>
            </>
          )}
          {status === 'errored' && (
            <>
              <Typography variant="h4" gutterBottom>
                Error
              </Typography>
              <Typography variant="body1" gutterBottom>
                Unable to verify your booking status. Please refresh the page
                and try again.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => window.location.reload()}
              >
                Refresh
              </Button>
            </>
          )}
        </Container>
      </Box>
    </ThemeProvider>
  )
}

export default CheckoutSuccessPage
